import React from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useWatch, useFormContext } from "react-hook-form";
import { useRecordContext } from "react-admin";

const plateStyles = {
  private: { backgroundColor: "#ededed", borderColor: "#666" },
  commercial: { backgroundColor: "#007ac2", borderColor: "#004a70" },
  transport: { backgroundColor: "#ffcc00", borderColor: "#333" }, // Yellow background
};

export const CarPlateField = ({ source, type = "transport", showEnglish = false }) => {
  const record = useRecordContext(); // Used for lists (React-Admin)
  const formContext = useFormContext(); // Used for forms
  const isInForm = !!formContext; // Check if inside a form

  // Use `useWatch` if inside a form, otherwise fallback to `record`
  const value = isInForm
    ? useWatch({ name: source, defaultValue: record?.[source] || "" }) // ✅ Works inside Forms
    : record?.[source] || ""; // ✅ Works inside Lists

  const parseValue = (value) => {
    if (!value) return { number: "", letters: ["", "", ""] };
    const [number, ...letters] = value.split(" ");
    return { number, letters };
  };

  const { number, letters } = parseValue(value);

  const arabicLetters = ["أ", "ب", "ح", "د", "ر", "س", "ص", "ط", "ع", "ق", "ك", "ل", "م", "ن", "ه", "و", "ي"];
  const englishLetters = ["A", "B", "H", "D", "R", "S", "T", "G", "K", "L", "M", "N", "H", "W", "Y"];

  const plateStyle = plateStyles[type] || plateStyles.transport;

  return (
    <div style={{ justifyContent: "center", paddingTop: "10px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: 0,
          width: "100%",
          margin: "auto",
          maxWidth: "140px",
          minHeight: "40px",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          backgroundColor: "#fff",
          direction: "rtl",
          border: `2px solid ${plateStyle.borderColor}`,
        }}
      >
        <Table sx={{ width: "100%", borderCollapse: "collapse", textAlign: "center" }}>
          <TableBody>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  width: "10px",
                  backgroundImage: "url(./ksa-logo.png)",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: plateStyle.backgroundColor,
                }}
              />
              {letters.map((letter, index) => (
                <TableCell
                  key={`arabic-${index}`}
                  sx={{
                    padding: "5px",
                    borderBottom: `2px solid ${plateStyle.borderColor}`,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold", fontFamily: "Arial, sans-serif" }}>
                    {letter}
                  </Typography>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  padding: "2px",
                  backgroundColor: "#fff",
                  borderBottom: `2px solid ${plateStyle.borderColor}`,
                  borderRight: `2px solid ${plateStyle.borderColor}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  {number}
                </Typography>
              </TableCell>
            </TableRow>

            {showEnglish && (
              <TableRow>
                {letters.map((letter, index) => (
                  <TableCell
                    key={`english-${index}`}
                    sx={{
                      padding: "2px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold", fontFamily: "Arial, sans-serif" }}>
                      {englishLetters[arabicLetters.indexOf(letter)] || ""}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    padding: "2px",
                    backgroundColor: "#fff",
                    borderRight: `2px solid ${plateStyle.borderColor}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontFamily: "Arial, sans-serif",
                    }}
                  >
                    {number}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
};
